import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { message } from "antd";
import Events from "components/events";
import { errorNotification } from "components/request-result";
import { Localize } from "components/service";
import {
    STAFF_CHECK_QR_STATUS,
    getStaffCheckQrBaseStatus,
    isStaffCheckQrLoading,
    resolveStaffCheckQrStatus,
} from "../helpers/staff-check-qr-helper";
import { getEventRoleIds } from "../helpers/staff-check-in-setup-helper";
import useCheckInPositionDate from "components/events/hooks/use-check-in-position-date";


const readGeneratedCoordinatesKey = (eventID) => {
    if (!eventID) return null;

    try
    {
        return window.localStorage.getItem(Events.Helpers.CheckQr.getCheckQrStorageKey(eventID));
    } catch (error)
    {
        return null;
    }
};

const writeGeneratedCoordinatesKey = (eventID, coordinatesKey) => {
    if (!eventID || !coordinatesKey) return;

    try
    {
        window.localStorage.setItem(
            Events.Helpers.CheckQr.getCheckQrStorageKey(eventID),
            coordinatesKey
        );
    } catch (error)
    {
        // QR freshness remains available for the current page even when storage is blocked.
    }
};

const removeGeneratedCoordinatesKey = (eventID) => {
    if (!eventID) return;

    try
    {
        window.localStorage.removeItem(Events.Helpers.CheckQr.getCheckQrStorageKey(eventID));
    } catch (error)
    {
        // The server QR URL remains the primary source of truth.
    }
};

const useStaffCheckQr = (event) => {
    const eventID = event?.id;
    const positionDate = useCheckInPositionDate({ eventID });
    const rawQrUrl = event?.getCheckQrCodeUrl || null;
    const currentCoordinatesKey = Events.Helpers.CheckQr.getCheckQrCoordinatesKey(event);
    const hasValidLocation = Events.Helpers.CheckLocation.hasMeaningfulCheckCoordinates(
        event?.check_latitude,
        event?.check_longitude
    ) && Events.Helpers.CheckLocation.isValidCheckRadius(event?.check_radius);
    const canGenerate = hasValidLocation && getEventRoleIds(event).length > 0;
    const eventSnapshotRef = useRef({ hasValidLocation, currentCoordinatesKey });
    const activeEventIDRef = useRef(eventID);
    const mountedRef = useRef(true);
    const requestRef = useRef(null);
    const locationSavingRef = useRef(false);
    const locationSaveCountRef = useRef(0);
    const [ generatedCoordinates, setGeneratedCoordinates ] = useState({
        eventID: null,
        key: null,
    });
    const [ operationStatus, setOperationStatus ] = useState(null);
    const [ locationSaving, setLocationSaving ] = useState(false);
    const [ qrRevision, setQrRevision ] = useState({ eventID: null, value: null });

    eventSnapshotRef.current = { hasValidLocation, currentCoordinatesKey };
    activeEventIDRef.current = eventID;

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            requestRef.current = null;
        };
    }, []);

    useLayoutEffect(() => {
        requestRef.current = null;
        setOperationStatus(null);
        locationSaveCountRef.current = 0;
        locationSavingRef.current = false;
        setLocationSaving(false);
    }, [ eventID ]);

    useEffect(() => {

        if (!eventID)
        {
            setGeneratedCoordinates({ eventID: null, key: null });
            return;
        }

        if (!rawQrUrl)
        {
            removeGeneratedCoordinatesKey(eventID);
            setGeneratedCoordinates({ eventID, key: null });
            return;
        }

        const storedKey = readGeneratedCoordinatesKey(eventID);
        const snapshot = eventSnapshotRef.current;
        const initialKey = storedKey || (
            snapshot.hasValidLocation ? snapshot.currentCoordinatesKey : null
        );

        if (!storedKey && initialKey)
        {
            writeGeneratedCoordinatesKey(eventID, initialKey);
        }

        setGeneratedCoordinates({ eventID, key: initialKey });
    }, [ eventID, rawQrUrl ]);

    useEffect(() => {
        if (!eventID || !rawQrUrl)
        {
            Events.Helpers.CheckQr.removeCheckQrRevision(eventID);
            setQrRevision({ eventID, value: null });
            return;
        }

        setQrRevision({
            eventID,
            value: Events.Helpers.CheckQr.readCheckQrRevision(eventID),
        });
    }, [ eventID, rawQrUrl ]);

    const storedCoordinatesKey = readGeneratedCoordinatesKey(eventID);
    const generatedCoordinatesKey = generatedCoordinates.eventID === eventID ?
        generatedCoordinates.key :
        rawQrUrl ? storedCoordinatesKey || (canGenerate ? currentCoordinatesKey : null) : null;
    const activeQrRevision = qrRevision.eventID === eventID ?
        qrRevision.value :
        Events.Helpers.CheckQr.readCheckQrRevision(eventID);
    const qrUrl = Events.Helpers.CheckQr.withCheckQrRevision(rawQrUrl, activeQrRevision);
    const baseStatus = getStaffCheckQrBaseStatus({
        qrUrl: rawQrUrl,
        hasValidLocation,
        generatedCoordinatesKey,
        currentCoordinatesKey,
    });
    const status = resolveStaffCheckQrStatus(baseStatus, operationStatus);

    const { _setEventCheckQrGenerate } = Events.Hooks.checkQrGenerate({
        notifySuccess: false,
        notifyError: false,
    });

    const generateQr = useCallback(({ coordinatesKey, location } = {}) => {
        const requestEvent = location || event;
        const requestCanGenerate = Events.Helpers.CheckLocation.hasMeaningfulCheckCoordinates(
            requestEvent?.check_latitude,
            requestEvent?.check_longitude
        ) &&
            Events.Helpers.CheckLocation.isValidCheckRadius(requestEvent?.check_radius) &&
            getEventRoleIds(requestEvent).length > 0;

        if (
            !mountedRef.current ||
            activeEventIDRef.current !== eventID ||
            !eventID ||
            !requestCanGenerate ||
            (locationSavingRef.current && !location)
        )
        {
            return Promise.resolve(null);
        }

        if (requestRef.current?.eventID === eventID)
        {
            return requestRef.current.promise;
        }

        const targetCoordinatesKey = coordinatesKey || currentCoordinatesKey;
        const regeneration = Boolean(rawQrUrl);
        const inProgressStatus = regeneration ?
            STAFF_CHECK_QR_STATUS.REGENERATING :
            STAFF_CHECK_QR_STATUS.GENERATING;
        const failedStatus = regeneration ?
            STAFF_CHECK_QR_STATUS.REGENERATION_FAILED :
            STAFF_CHECK_QR_STATUS.GENERATION_FAILED;

        setOperationStatus(inProgressStatus);

        const requestRecord = { eventID, promise: null };
        const request = _setEventCheckQrGenerate({ variables: { ee_id: eventID } })
            .then((result) => {
                const nextQrUrl = result?.data?.eeCheckQrGenerate?.exhibitionEvent?.getCheckQrCodeUrl;

                if (!nextQrUrl)
                {
                    if (mountedRef.current && activeEventIDRef.current === eventID)
                    {
                        message.error(<Localize>CHECK_QR.Text_NoQrUrl</Localize>);
                        setOperationStatus(failedStatus);
                    }

                    return null;
                }

                if (mountedRef.current && activeEventIDRef.current === eventID)
                {
                    const nextRevision = regeneration && nextQrUrl === rawQrUrl ?
                        String(Date.now()) :
                        null;

                    writeGeneratedCoordinatesKey(eventID, targetCoordinatesKey);
                    setGeneratedCoordinates({ eventID, key: targetCoordinatesKey });
                    if (nextRevision)
                    {
                        Events.Helpers.CheckQr.writeCheckQrRevision(eventID, nextRevision);
                    } else
                    {
                        Events.Helpers.CheckQr.removeCheckQrRevision(eventID);
                    }
                    setQrRevision({ eventID, value: nextRevision });
                    setOperationStatus(null);

                    return Events.Helpers.CheckQr.withCheckQrRevision(nextQrUrl, nextRevision);
                }

                return null;
            })
            .catch((error) => {
                if (mountedRef.current && activeEventIDRef.current === eventID)
                {
                    errorNotification(error);
                    setOperationStatus(failedStatus);
                }

                return null;
            })
            .finally(() => {
                if (requestRef.current === requestRecord)
                {
                    requestRef.current = null;
                }
            });

        requestRecord.promise = request;
        requestRef.current = requestRecord;

        return request;
    }, [
        _setEventCheckQrGenerate,
        currentCoordinatesKey,
        event,
        eventID,
        rawQrUrl,
    ]);

    const setLocationSavingState = useCallback((saving, savingEventID) => {
        if (
            !mountedRef.current ||
            (savingEventID && activeEventIDRef.current !== savingEventID)
        )
        {
            return;
        }

        locationSaveCountRef.current = saving ?
            locationSaveCountRef.current + 1 :
            Math.max(0, locationSaveCountRef.current - 1);
        const nextSaving = locationSaveCountRef.current > 0;

        locationSavingRef.current = nextSaving;
        setLocationSaving(nextSaving);
    }, []);
    const isGenerationInFlight = useCallback(() => (
        requestRef.current?.eventID === activeEventIDRef.current
    ), []);

    return {
        status,
        positionDate,
        qrUrl,
        hasValidLocation,
        canGenerate,
        currentCoordinatesKey,
        generatedCoordinatesKey,
        loading: isStaffCheckQrLoading(status),
        locationSaving,
        setLocationSaving: setLocationSavingState,
        isGenerationInFlight,
        generateQr,
    };
};

export default useStaffCheckQr;
